// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Centralized date/time formatting utilities for IE/UK locale.
 *
 * All functions use `en-IE` locale to produce day-first dates and 24-hour time,
 * matching Ireland and UK conventions.
 */

const LOCALE = 'en-IE';

/**
 * Date only – short month (tables, lists, cards).
 * Output: "15 Jan 2026"
 */
export function formatDate(dateString?: string | null): string {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Date only – numeric short (compact tables, document lists).
 * Output: "12/02/26"
 */
export function formatDateShort(dateString?: string | null): string {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

/**
 * Date only – long month (detail pages, documents).
 * Output: "15 January 2026"
 */
export function formatDateLong(dateString?: string | null): string {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Time only (activity feeds, logs).
 * Output: "14:30"
 */
export function formatTime(dateString?: string | null): string {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return d.toLocaleTimeString(LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Date + time (audit logs, status updates, timestamps).
 * Output: "15 Jan 2026, 14:30"
 */
export function formatDateTime(dateString?: string | null): string {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Date + time with seconds (legal/signing events).
 * Output: "15 January 2026, 14:30:45"
 */
export function formatDateTimeLong(dateString?: string | null): string {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString(LOCALE, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/**
 * Relative time (notifications, activity feeds).
 * Output: "Just now", "5 min ago", "2 hours ago", "3 days ago",
 *         or falls back to formatDate for older dates.
 */
export function formatRelativeTime(dateString?: string | null): string {
  if (!dateString) return 'Never';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  return formatDate(dateString);
}

/**
 * Compact relative time for tight UI spaces.
 * Output: "Just now", "5m ago", "2h ago", "3d ago",
 *         or falls back to formatDate for older dates.
 */
export function formatRelativeTimeShort(dateString?: string | null): string {
  if (!dateString) return 'Never';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

/**
 * Date grouping for activity feeds.
 * Output: "Today", "Yesterday", "This Week", or formatted date.
 */
export function getDateGroup(dateString?: string | null): string {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  if (d >= weekAgo) return 'This Week';
  return formatDate(dateString);
}

/**
 * Expiry-aware relative time for tokens/deadlines.
 * Handles both past (expired) and future (expires in) dates.
 * Output: "Expires today", "Expires in 3 days", "Expired 2 days ago"
 */
export function formatExpiryRelativeTime(dateString?: string | null): string {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;

  const now = new Date();
  const diff = d.getTime() - now.getTime();
  const absDays = Math.abs(Math.floor(diff / 86_400_000));

  if (diff < 0) {
    if (absDays === 0) return 'Expired today';
    return `Expired ${absDays} day${absDays !== 1 ? 's' : ''} ago`;
  }
  if (absDays === 0) return 'Expires today';
  return `Expires in ${absDays} day${absDays !== 1 ? 's' : ''}`;
}
